import NativeAd from "../ads/Native";
import styles from "./ArticleRelatedContentList.module.scss";
import {
  RelatedContentAuthorFragment,
  RelatedContentRiverFragment,
} from "types/api";
import { gql } from "utils/urql";
import Authors from "components/Authors";
import Image from "components/Image";

const AuthorWrapper = ({
  articleIndex,
  displayName,
  url,
  heading,
}: RelatedContentAuthorFragment & {
  articleIndex: number;
  heading: string;
}) => {
  return (
    <a
      className={styles.itemLink}
      href={url}
      data-action={`click link - ${heading.toLowerCase()} - author ${
        articleIndex + 1
      }`}
      data-label={url}
    >
      <span dangerouslySetInnerHTML={{ __html: displayName }} />
    </a>
  );
};

const RecircNativeAd = () => {
  return (
    <NativeAd
      targetingPos="native-article-related"
      targetingNative="native"
      lazyLoadScreens={3}
      className="s-native s-native--short-title s-native--standard s-native--small s-native--streamline"
    />
  );
};

const ArticleRelatedContentList = ({
  displayName,
  articles,
  count,
}: {
  displayName: string;
  articles: RelatedContentRiverFragment;
  count: number;
}) => {
  const edges =
    articles.edges.length > 3 ? articles.edges.slice(0, 3) : articles.edges;
  return (
    <section className={styles.root}>
      <h2 className={styles.heading}>{displayName}</h2>
      <ul className={styles.list}>
        {edges.slice(0, 3).map((article, i) => {
          return (
            <li className={styles.listItem} key={`article-recirc-rr-${i}`}>
              {/* Insert native ad before last article so the last article can be hidden with .ad-loaded + .content if an ad loads */}
              {i === edges.length - 1 && <RecircNativeAd />}
              <div
                className={styles.content}
                data-view-action={`view link - ${displayName.toLowerCase()} ${count} - item ${
                  i + 1
                }`}
                data-view-label={article.node.url}
              >
                {article.node.riverImage && (
                  <figure className={styles.figure}>
                    <a
                      className={styles.link}
                      href={article.node.url}
                      title={`Read More: ${article.node.title}`}
                      data-action={`click link - ${displayName.toLowerCase()} - image ${
                        i + 1
                      }`}
                      data-label={article.node.url}
                    >
                      <picture className={styles.picture}>
                        <Image
                          lazy={true}
                          width={article.node.riverImage.width}
                          height={article.node.riverImage.height}
                          className={styles.image}
                          alt={article.node.riverImage.altText || ""}
                          src={article.node.riverImage.url}
                          srcSet={article.node.riverImage.srcSet}
                        />
                      </picture>
                    </a>
                  </figure>
                )}
                <div className={styles.textWrapper}>
                  {article.node.title && (
                    <h3 className={styles.title}>
                      <a
                        className={styles.link}
                        href={article.node.url}
                        dangerouslySetInnerHTML={{
                          __html: article.node.title,
                        }}
                        data-action={`click link - ${displayName.toLowerCase()} - title ${
                          i + 1
                        }`}
                        data-label={article.node.url}
                      ></a>
                    </h3>
                  )}
                  {article.node.authors && (
                    <address className={styles.byline}>
                      <Authors
                        authors={article.node.authors}
                        component={AuthorWrapper}
                        articleIndex={i}
                        heading={displayName}
                      />
                    </address>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
};

ArticleRelatedContentList.fragment = gql`
  fragment relatedContentAuthor on Author {
    url
    displayName
    id
    slug
  }

  fragment relatedContentRiver on RiverConnection {
    edges {
      node {
        url
        title
        authors {
          ...relatedContentAuthor
        }
        riverImage(width: 90, height: 90, sizes: ["90w", "180w"]) {
          id
          url
          srcSet
          width
          height
          altText
        }
      }
    }
  }
`;

export default ArticleRelatedContentList;
